import { useMemo, useState } from 'react';
import { ActionIcon, Alert, Badge, Center, Group, Loader, SimpleGrid, Table, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconCash, IconReceipt, IconReceipt2, IconWallet } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { SectionCard } from './SectionCard';
import { TablePagination } from './TablePagination';
import { FetchOverlay } from './FetchOverlay';
import { PaymentReceiptModal } from './PaymentReceipt';
import { formatDate, formatSom } from '../lib/format';
import { monthLabel } from '../lib/month';

interface Payment {
  id: string;
  amount: number;
  paidAt: string;
  forMonth?: string | null;
  method: string;
  /** To'ldirilgan bo'lsa — bu qator qaytarilgan pul (summasi manfiy) */
  reversalOfId?: string | null;
  /** Bu to'lovni yopgan qaytarish qatori (bo'lsa) — pul qaytarilgan */
  reversal?: { id: string; reversalReason?: string | null } | null;
  student?: { id: string; fullName: string };
}

/**
 * Ota-ona sahifasidagi "To'lovlar" tabi — shu ota-onaning farzandlari uchun qilingan
 * to'lovlar tarixi. `Payment` da to'lovchi maydoni yo'q (to'lov o'quvchiga, `createdBy`
 * — kassir), shuning uchun "ota-ona to'lovi" = uning farzandlari to'lovi:
 * `GET /payments?parentId=` (server `student.parentId` bo'yicha filtrlaydi).
 *
 * Jami summa `/payments/summary` dan — AYNI filtr, ya'ni jadval bilan mos. Storno
 * qatorlari manfiy summa bilan keladi va yig'indini o'zi to'g'rilaydi.
 */
export function ParentPayments({ parentId, childIds }: { parentId: string; childIds: string[] }) {
  const { t } = useTranslation();
  const monthNames = t('monthsShort', { returnObjects: true }) as string[];
  const [receiptId, setReceiptId] = useState<string | null>(null);

  const METHODS: Record<string, string> = {
    CASH: t('paymentsPage.methodCash'),
    CARD: t('paymentsPage.methodCard'),
    TRANSFER: t('paymentsPage.methodTransfer'),
    OTHER: t('paymentsPage.methodOther'),
  };

  // Qaytarilgan pulning manfiy qatori yashiriladi — asl to'lov "Qaytarilgan" belgisi bilan turadi
  const params = { parentId, hideReversals: '1' };
  const { items: payments, isLoading, fetching, pages, page, setPage, total, limit, setLimit } =
    usePagedQuery<Payment>('parent-payments', '/payments', params, 20);
  const { data: summary } = useQuery({
    queryKey: ['parent-payments-summary', parentId],
    queryFn: async () => (await api.get<{ total: number; count: number }>('/payments/summary', { params })).data,
  });

  /* HIMOYA: `parentId` ni bilmaydigan (eski) server filtrni jimgina tashlab, butun
     maktab to'lovlarini qaytaradi. Begona o'quvchi qatori ko'rinsa — noto'g'ri
     moliyaviy ma'lumot ko'rsatilmaydi, ogohlantirish chiqadi. */
  const childSet = useMemo(() => new Set(childIds), [childIds]);
  const serverEski = payments.some((p) => p.student && !childSet.has(p.student.id));

  if (serverEski) {
    return (
      <Alert color="orange" icon={<IconAlertTriangle size={18} />} title={t('parentDetail.paymentsUnsupportedTitle')}>
        {t('parentDetail.paymentsUnsupported')}
      </Alert>
    );
  }

  return (
    <>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <SectionCard title={t('parentDetail.paymentsTotal')} icon={IconWallet} color="green">
          <Text size="xl" fw={700}>{formatSom(summary?.total ?? 0)}</Text>
        </SectionCard>
        <SectionCard title={t('parentDetail.paymentsCount')} icon={IconReceipt2} color="blue">
          <Text size="xl" fw={700}>{summary?.count ?? 0}</Text>
        </SectionCard>
      </SimpleGrid>

      <SectionCard title={t('parentDetail.tabPayments')} icon={IconCash} p={0} pos="relative">
        <FetchOverlay visible={fetching} />
        {isLoading ? (
          <Center h={160}><Loader /></Center>
        ) : !payments.length ? (
          <Center h={140}><Text c="dimmed">{t('studentDetailPage.noPayments')}</Text></Center>
        ) : (
          <Table.ScrollContainer minWidth={720}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('parentDetail.child')}</Table.Th>
                  <Table.Th>{t('fields.amount')}</Table.Th>
                  <Table.Th>{t('paymentsPage.method')}</Table.Th>
                  <Table.Th>{t('fields.month')}</Table.Th>
                  <Table.Th>{t('fields.date')}</Table.Th>
                  <Table.Th w={70} ta="right">{t('common.actions')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {payments.map((p) => {
                  const storno = !!p.reversalOfId;
                  const qaytarilgan = !!p.reversal;
                  return (
                    <Table.Tr key={p.id}>
                      <Table.Td fw={500}>{p.student?.fullName ?? '—'}</Table.Td>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Text fw={600} size="sm" c={p.amount < 0 ? 'red.7' : qaytarilgan ? 'dimmed' : 'green.7'} td={qaytarilgan ? 'line-through' : undefined}>{formatSom(p.amount)}</Text>
                          {storno && <Badge size="xs" color="red" variant="light">{t('parentDetail.storno')}</Badge>}
                          {qaytarilgan && (
                            <Tooltip label={p.reversal?.reversalReason ?? ''} disabled={!p.reversal?.reversalReason} withArrow>
                              <Badge size="sm" color="orange" variant="light">{t('paymentsPage.badgeReversed')}</Badge>
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>{METHODS[p.method] ?? p.method}</Table.Td>
                      <Table.Td>{p.forMonth ? monthLabel(p.forMonth, monthNames) : '—'}</Table.Td>
                      <Table.Td>{formatDate(p.paidAt)}</Table.Td>
                      <Table.Td ta="right">
                        <Tooltip label={t('receipt.open')} withArrow>
                          <ActionIcon variant="light" onClick={() => setReceiptId(p.id)} aria-label={t('receipt.open')}>
                            <IconReceipt size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>
      <TablePagination page={page} pages={pages} total={total} limit={limit} onPage={setPage} onLimit={setLimit} />
      <PaymentReceiptModal paymentId={receiptId} onClose={() => setReceiptId(null)} />
    </>
  );
}
